import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  compose, filter, get,
  groupBy, includes, last,
  map, reject, sortBy,
  size, sumBy, toNumber,
  toString,
} from 'lodash/fp';
import {
  Checkbox, Chip, FormControl, InputLabel, MenuItem, OutlinedInput, Paper, Popper, Select,
} from '@material-ui/core';
import { ChevronRight, GroupOutlined } from '@material-ui/icons';

const S = {
  Option: styled.div`
    display: flex;
    align-items: center;
  `,
  GroupOutlined: styled(GroupOutlined)`
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 1.25rem;
  `,
  Label: styled.div`
    position: relative;
    left: -6px;
  `,
  Chip: styled(({ error, secondary, ...otherProps }) => <Chip {...otherProps} />)`
    height: auto;
    padding: 0.5px 6px;
    border-radius: 4px;
    color: ${({ theme }) => theme.palette.primary.contrastText};
    background-color: ${({ theme }) => theme.palette.blueGrey[600]};
    font-size: 0.75rem;
    font-weight: 500;
    text-align: center;
    .MuiChip-label {
      padding: 0;
    }
    :not(:last-child) {
      margin-right: 8px;
    }
    ${({ secondary }) => secondary && css`
      background-color: ${({ theme }) => theme.palette.secondary.main};
    `}
    ${({ error, secondary }) => error && secondary && css`
      background-color: ${({ theme }) => theme.palette.error.main};
    `}
  `,
  Checkbox: styled(Checkbox)`
    padding: 4px;
    color: ${({ theme }) => theme.palette.grey[400]};
  `,
  Wrapper: styled.div`
    width: 100%;
    margin-top: ${({ margin = {} }) => margin.top};
    margin-left: ${({ margin = {} }) => margin.left};
    margin-bottom: ${({ margin = {} }) => margin.bottom};
    margin-right: ${({ margin = {} }) => margin.right};
  `,
  FormControl: styled(FormControl)`
    width: 100%;
  `,
  InputLabel: styled(InputLabel)`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  Select: styled(Select)`
    .MuiSelect-icon {
      right: 0.5rem;
    }
    .MuiSelect-select {
      display: flex;
      align-items: center;
      height: 40px;
      padding: 0 2rem 0 0.875rem;
      color: ${({ theme }) => theme.palette.grey[800]};
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
      font-weight: 400;
      background-color: transparent;
    }
    &.Mui-focused .MuiSelect-icon {
      color: ${({ theme }) => theme.palette.primary.main};
    }
    :hover {
      .icon {
        color: ${({ theme }) => theme.palette.primary.main};
      }
    }
    ${({ error, theme }) => error && css`
      .MuiSelect-icon {
        color: ${theme.palette.error.main};
      }
      :hover {
        .MuiSelect-icon {
          color: ${theme.palette.error.main};
        }
      }
      &.Mui-focused .MuiSelect-icon {
        color: ${theme.palette.error.main};
      }
    `}
  `,
  OutlinedInput: styled(({ classes, ...otherProps }) => (
    <OutlinedInput
      classes={{
        ...classes,
        notchedOutline: 'notchedOutline',
      }}
      {...otherProps}
    />
  ))`
    .notchedOutline {
      border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    }
  `,
  MenuItem: styled(MenuItem)`
    position: relative;
    min-width: 200px;
    min-height: 40px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
    &.Mui-selected:hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
  `,
  ChevronRight: styled(ChevronRight)`
    margin-left: auto;
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 1.25rem;
  `,
  Popper: styled(Popper)`
    z-index: 1300;
  `,
  Paper: styled(Paper)`
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(100%, -8px);
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.12);
  `,
  Suboptions: styled.ul`
    padding: 8px 0;
  `,
  HelperText: styled.p`
    margin-top: 4px;
    padding-left: 16px;
    color: ${({ theme }) => theme.palette.error.main};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
  `,
};

const GroupField = ({
  className, field, form, groups, id, label,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [active, setActive] = useState(false);
  const [labelWidth, setLabelWidth] = useState(0);

  const inputLabel = useRef(null);

  const measuredRef = useCallback((node) => {
    if (node) {
      inputLabel.current = node;
    }
  }, []);

  const options = useMemo(() => {
    const gradeGroups = compose(
      groupBy(clazzGroup => clazzGroup.grade),
      get('clazzGroups'),
    )(groups);

    return [
      ...map.convert({ cap: false })((gradeGroup, key) => ({
        id: `gradeGroup_${key}`,
        type: 'gradeGroup',
        value: key,
        label: (
          <S.Option>
            <S.GroupOutlined />
            <span>{`${key}학년 (${sumBy(g => g.contacts_count, gradeGroup)}명)`}</span>
          </S.Option>
        ),
        suboptions: map(clazz => ({
          id: `clazzGroup_${clazz.id}`,
          type: 'clazzGroup',
          value: clazz.id,
          chip: `${key}학년 ${clazz.class_name}반`,
          label: (
            <S.Label>
              <S.Checkbox
                color="primary"
                checked={includes(`clazzGroup_${clazz.id}`, map(value => value.id, field.value))}
              />
              {`${clazz.class_name}반 (${clazz.contacts_count}명)`}
            </S.Label>
          ),
        }), gradeGroup),
      }), gradeGroups),
      ...compose(
        map(customGroup => ({
          id: `customGroup_${customGroup.id}`,
          type: 'customGroup',
          value: customGroup.id,
          chip: `${customGroup.name}`,
          label: (
            <S.Label>
              <S.Checkbox
                color="primary"
                checked={includes(`customGroup_${customGroup.id}`, map(value => value.id, field.value))}
              />
              {`${customGroup.name} (${customGroup.contacts_count}명)`}
            </S.Label>
          ),
        })),
        reject(customGroup => customGroup.is_unspecified_group),
        get('customGroups'),
      )(groups),
    ];
  }, [field.value, groups]);

  const clazzGroupCount = useMemo(() => compose(
    size,
    filter(value => includes('clazzGroup', value.id)),
  )(field.value), [field.value]);

  const error = useMemo(() => !!(
    get(field.name, form.errors) && get(field.name, form.touched)
  ), [field.name, form.errors, form.touched]);

  const MenuProps = useMemo(() => ({
    onEntered: () => setActive(true),
    onExit: () => {
      setAnchorEl(null);
      setActive(false);
      form.setFieldTouched(field.name, true);
    },
  }), [field, form]);

  const handleGroupChange = useCallback((event) => {
    if (event.currentTarget.dataset.type === 'customGroup') {
      if (includes(last(event.target.value).id, map(value => value.id, field.value))) {
        form.setFieldValue(field.name, reject(value => value.id === last(event.target.value).id, field.value));
      } else {
        form.setFieldValue(field.name, map(group => ({
          chip: group.chip,
          id: group.id,
          type: group.type,
          value: group.value,
        }), event.target.value));
      }
    }
  }, [field, form]);
  const handleSubgroupChange = useCallback((event) => {
    event.stopPropagation();
    if (includes(event.currentTarget.dataset.id, map(value => value.id, field.value))) {
      form.setFieldValue(field.name, reject(value => value.id === event.currentTarget.dataset.id, field.value));
    } else {
      form.setFieldValue(field.name, [
        ...field.value,
        {
          chip: event.currentTarget.dataset.chip,
          id: event.currentTarget.dataset.id,
          type: event.currentTarget.dataset.type,
          value: toNumber(event.currentTarget.dataset.value),
        },
      ]);
    }
  }, [field, form]);
  const renderValue = useCallback(compose(
    map(selectedValue => (
      <S.Chip
        key={selectedValue.id}
        label={selectedValue.chip}
        secondary={selectedValue.type === 'clazzGroup'}
        error={clazzGroupCount > 1}
      />
    )),
    sortBy(['type']),
  ), [clazzGroupCount]);
  const showSuboptions = useCallback(event => setAnchorEl(event.currentTarget), []);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  return (
    <S.Wrapper margin={{ bottom: clazzGroupCount > 1 ? '8px' : '16px' }}>
      <S.FormControl
        className={className}
        variant="outlined"
        error={error}
      >
        {label && (
          <S.InputLabel ref={measuredRef} htmlFor={id} margin="dense">
            {label}
          </S.InputLabel>
        )}
        <S.Select
          renderValue={renderValue}
          value={field.value}
          onChange={handleGroupChange}
          MenuProps={MenuProps}
          input={<S.OutlinedInput labelWidth={labelWidth} name="select" id="select" />}
          error={error}
          multiple
        >
          {map(option => (
            <S.MenuItem
              key={option.id}
              id={option.id}
              value={option}
              data-type={option.type}
              onMouseOver={showSuboptions}
              onFocus={showSuboptions}
              disableRipple={!!option.suboptions}
            >
              {option.label}
              {option.suboptions && <S.ChevronRight />}
              {option.suboptions && active && (
                <S.Popper
                  placement="top-end"
                  anchorEl={anchorEl}
                  open={get('id', anchorEl) === toString(option.id)}
                >
                  <S.Paper>
                    <S.Suboptions>
                      {map(suboption => (
                        <S.MenuItem
                          key={suboption.id}
                          data-id={suboption.id}
                          data-type={suboption.type}
                          data-chip={suboption.chip}
                          data-value={suboption.value}
                          onClick={handleSubgroupChange}
                        >
                          {suboption.label}
                        </S.MenuItem>
                      ), option.suboptions)}
                    </S.Suboptions>
                  </S.Paper>
                </S.Popper>
              )}
            </S.MenuItem>
          ), options)}
        </S.Select>
        {clazzGroupCount > 1 && (
          <S.HelperText>
            {get(field.name, form.errors)}
          </S.HelperText>
        )}
      </S.FormControl>
    </S.Wrapper>
  );
};

GroupField.propTypes = {
  className: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.shape({}),
    touched: PropTypes.shape({}),
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
  }).isRequired,
  groups: PropTypes.shape({
    clazzGroups: PropTypes.arrayOf(PropTypes.shape({})),
    customGroups: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  id: PropTypes.string,
  label: PropTypes.string,
};

GroupField.defaultProps = {
  className: undefined,
  groups: null,
  id: undefined,
  label: undefined,
};

export default GroupField;
